import React, { useState } from 'react';
import { Eye, Bell, Shield, Zap, AlertCircle, TrendingUp, Clock, Target, CheckCircle, ChevronDown, DollarSign } from 'lucide-react';
import CTASection from '../../components/CTASection';

const TrademarkMonitoringPage = () => {
  const [openFAQ, setOpenFAQ] = useState(0);

  const monitoringLevels = [
    {
      name: 'Basic Monitoring',
      price: '$49',
      period: '/month',
      description: 'Essential monitoring for startups',
      features: [
        'USPTO trademark applications',
        'Monthly reports',
        'Email alerts',
        'Up to 1 trademark',
        'Basic threat assessment'
      ]
    },
    {
      name: 'Professional Monitoring',
      price: '$149',
      period: '/month',
      description: 'Comprehensive protection for growing brands',
      features: [
        'All Basic features',
        'Domain name registrations',
        'Social media monitoring',
        'Weekly reports',
        'Up to 5 trademarks',
        'Priority alerts',
        'Detailed threat analysis'
      ],
      popular: true
    },
    {
      name: 'Enterprise Monitoring',
      price: '$399',
      period: '/month',
      description: 'Complete protection for established brands',
      features: [
        'All Professional features',
        'E-commerce platform monitoring',
        'International trademark offices',
        'Real-time alerts',
        'Unlimited trademarks',
        'Dedicated account manager',
        'Enforcement assistance',
        'Custom reporting'
      ]
    }
  ];

  const whatWeMonitor = [
    {
      category: 'Government Filings',
      items: ['USPTO trademark applications', 'State trademark registrations', 'International trademark offices (WIPO)', 'Business name registrations']
    },
    {
      category: 'Online Presence',
      items: ['Domain name registrations (.com, .net, .org, etc.)', 'Social media accounts (Instagram, Facebook, Twitter, TikTok)', 'Business directories and listings', 'Google My Business profiles']
    },
    {
      category: 'E-Commerce',
      items: ['Amazon seller accounts', 'eBay listings', 'Etsy shops', 'Shopify stores', 'Other marketplaces']
    },
    {
      category: 'Advertising',
      items: ['Google Ads campaigns', 'Social media advertising', 'Display advertising networks', 'Sponsored content']
    }
  ];

  const threatLevels = [
    {
      level: 'Critical',
      color: 'red',
      description: 'Identical or near-identical mark in same industry',
      action: 'Immediate cease & desist recommended',
      examples: ['Same name, same industry', 'Identical logo design', 'Exact domain with your brand']
    },
    {
      level: 'High',
      color: 'orange',
      description: 'Similar mark in related industry or identical in different industry',
      action: 'Opposition filing or legal consultation',
      examples: ['Similar name, related products', 'Close logo similarity', 'Confusing domain variant']
    },
    {
      level: 'Medium',
      color: 'yellow',
      description: 'Somewhat similar mark or unrelated industry',
      action: 'Monitor closely, consider action if escalates',
      examples: ['Partial name match', 'Same industry, different name', 'Similar social handle']
    },
    {
      level: 'Low',
      color: 'green',
      description: 'Minimal similarity or clearly different context',
      action: 'Continue monitoring, no immediate action needed',
      examples: ['Different spelling', 'Unrelated industry', 'Generic usage']
    }
  ];

  const caseStudies = [
    {
      title: 'Fashion Brand Stops Chinese Counterfeiter',
      scenario: 'Luxury fashion brand with $50M annual revenue',
      detection: 'Our monitoring detected an Amazon seller using identical logo and brand name for knockoff products',
      action: 'Immediate cease & desist sent, Amazon seller account suspended within 48 hours',
      result: 'Prevented estimated $2M in counterfeit sales and brand dilution. Client awarded $500K in damages.',
      timeframe: '2 days from detection to resolution'
    },
    {
      title: 'Tech Startup Blocks Competitor Domain',
      scenario: 'SaaS company discovered competitor registered confusingly similar domain',
      detection: 'Weekly domain monitoring alert flagged registration of [brandname]solutions.com',
      action: 'UDRP complaint filed with supporting trademark registration',
      result: 'Domain transferred to client within 60 days. Competitor forced to rebrand.',
      timeframe: '60 days from detection to domain transfer'
    },
    {
      title: 'Restaurant Chain Protects Regional Expansion',
      scenario: 'Regional restaurant planning national expansion',
      detection: 'Identified 3 businesses in different states using same name',
      action: 'Negotiated coexistence agreements with 2, cease & desist to third',
      result: 'Secured rights for 47-state expansion, avoided costly litigation',
      timeframe: '4 months for complete resolution'
    }
  ];

  const statistics = [
    { number: '73%', label: 'of trademark owners discover infringement too late', source: 'USPTO Study 2023' },
    { number: '$2.3M', label: 'Average cost of trademark litigation', source: 'INTA Report 2024' },
    { number: '6 months', label: 'Average time for infringer to establish market presence', source: 'IP Watchdog' },
    { number: '89%', label: 'of consumers confused by similar brands', source: 'Brand Protection Survey' }
  ];

  const enforcementOptions = [
    {
      option: 'Cease & Desist Letter',
      description: 'Formal legal demand to stop using your trademark',
      cost: '$500-$1,500',
      effectiveness: '70% success rate',
      timeframe: '2-4 weeks',
      bestFor: 'Clear infringement cases, willing infringers'
    },
    {
      option: 'USPTO Opposition',
      description: 'File opposition to prevent trademark registration',
      cost: '$1,500-$5,000',
      effectiveness: '85% success rate with evidence',
      timeframe: '6-12 months',
      bestFor: 'Pending trademark applications'
    },
    {
      option: 'UDRP Complaint',
      description: 'Domain dispute resolution for cybersquatting',
      cost: '$1,300-$3,000',
      effectiveness: '90% success rate',
      timeframe: '2-3 months',
      bestFor: 'Domain name infringement'
    },
    {
      option: 'Platform Takedown',
      description: 'Report infringement to e-commerce platforms',
      cost: '$0-$500',
      effectiveness: '95% success rate',
      timeframe: '24-72 hours',
      bestFor: 'Amazon, eBay, social media violations'
    },
    {
      option: 'Trademark Litigation',
      description: 'Federal lawsuit for trademark infringement',
      cost: '$50,000-$500,000',
      effectiveness: 'Varies, high with strong evidence',
      timeframe: '12-36 months',
      bestFor: 'Serious infringement, damages needed'
    }
  ];

  const faqs = [
    {
      question: 'When should I start monitoring my trademark?',
      answer: 'Begin monitoring as soon as your trademark application is filed or when you start using your mark in commerce. Early detection of infringement is crucial - the longer an infringer operates, the harder and more expensive it becomes to enforce your rights. Many businesses wait until after registration, but this leaves a critical gap where infringers can establish themselves.'
    },
    {
      question: 'How often will I receive monitoring reports?',
      answer: 'Report frequency depends on your monitoring level: Basic plans receive monthly reports, Professional plans get weekly reports, and Enterprise clients receive real-time alerts plus customized reporting schedules. Critical threats are always reported immediately regardless of plan level.'
    },
    {
      question: "What happens when you detect an infringement?",
      answer: 'We immediately alert you via email with detailed information about the potential infringer, including their registration details, similarity assessment, and threat level. Our legal team provides recommendations for action, from monitoring the situation to sending cease & desist letters. You decide the response strategy with our guidance.'
    },
    {
      question: 'Can monitoring prevent trademark infringement?',
      answer: 'Monitoring cannot prevent infringement, but it enables early detection which is the next best thing. Early detection means infringers have less time to establish their brand, invest in marketing, or build customer confusion. This makes enforcement significantly easier and less costly. In our experience, 70% of infringers stop when contacted early with a cease & desist letter.'
    },
    {
      question: 'Is monitoring required after trademark registration?',
      answer: "While not legally required, monitoring is essential for maintaining your trademark rights. Trademark law requires you to actively police your mark. Failure to enforce your rights against infringers can result in trademark abandonment or weakening. Courts have ruled against trademark owners who knew about infringement but failed to act, considering this implied consent." },
    {
      question: 'What if I find an infringer? Do you help with enforcement?',
      answer: 'Yes! Our Professional and Enterprise plans include enforcement assistance. We can draft cease & desist letters, file USPTO oppositions, handle platform takedown requests, and refer you to litigation attorneys for serious cases. We guide you through the entire enforcement process and handle much of the legwork.'
    },
    {
      question: 'How is this different from doing my own Google searches?',
      answer: 'Manual Google searches only catch obvious, current infringement and miss critical sources like pending USPTO applications, newly registered domains, private social media accounts, and international filings. Our AI scans databases Google cannot access, including trademark office backfiles, WHOIS records, and proprietary databases. We also provide legal analysis of similarity and threat level, which search engines cannot do.'
    },
    {
      question: 'Can I monitor competitors to see their trademark activity?',
      answer: 'Yes! Monitoring can track competitor trademark filings to understand their brand strategy and expansion plans. This competitive intelligence helps you anticipate market moves, identify potential conflicts before they arise, and protect your market position. Many of our enterprise clients use monitoring for both defensive protection and competitive intelligence.'
    }
  ];

  return (
    <div>
      {/* Hero */}
      <div className="bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-block bg-[#FF6B35]/20 text-[#FF6B35] px-4 py-2 rounded-full text-sm font-semibold mb-6">
              24/7 Brand Protection
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Trademark <span className="text-[#FF6B35]">Monitoring Services</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Continuous surveillance to protect your brand from infringement with real-time alerts and expert enforcement support
            </p>
            <div className="flex gap-4">
              <button className="bg-[#FF6B35] hover:bg-[#FF8C42] text-white font-bold px-8 py-4 rounded-lg transition-all">
                Start Monitoring Now
              </button>
              <button className="border-2 border-white text-white hover:bg-white hover:text-black font-bold px-8 py-4 rounded-lg transition-all">
                View Sample Alert
              </button>
            </div>
            <div className="flex items-center gap-6 mt-8 text-sm">
              <span className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-[#FF6B35]" />
                50M+ Records Monitored
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#FF6B35]" />
                Real-Time Alerts
              </span>
              <span className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#FF6B35]" />
                Expert Analysis
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {statistics.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-[#FF6B35] mb-2">{stat.number}</div>
                <div className="text-gray-700 mb-1">{stat.label}</div>
                <div className="text-xs text-gray-500">{stat.source}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* What Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block bg-[#FF6B35]/10 text-[#FF6B35] px-4 py-2 rounded-full text-sm font-semibold mb-6">
                What is Trademark Monitoring?
              </div>
              <h2 className="text-4xl font-bold text-black mb-6">Your Brand's 24/7 Security Guard</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Trademark monitoring is an ongoing surveillance service that continuously watches for potential infringements of your registered trademark. Our advanced monitoring system scans USPTO filings, domain registrations, social media platforms, and online marketplaces to identify unauthorized use of your brand.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                When a potential conflict is detected, you receive immediate alerts with detailed information about the infringer, similarity assessment, and recommended actions. This early warning system allows you to take swift action to protect your brand rights before infringers become established.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Think of it as having a dedicated legal team watching your trademark around the clock, across every platform and jurisdiction where your brand could be at risk.
              </p>
              <p className="text-lg font-semibold text-black">
                Registration is just the beginning—ongoing monitoring is what keeps your brand truly protected.
              </p>
            </div>
            <div className="bg-gradient-to-br from-[#FF6B35]/10 to-[#FF8C42]/10 rounded-2xl p-8 border border-[#FF6B35]/20">
              <Eye className="w-16 h-16 text-[#FF6B35] mb-6" />
              <h3 className="text-2xl font-bold text-black mb-6">What We Monitor:</h3>
              <div className="space-y-6">
                {whatWeMonitor.map((group, index) => (
                  <div key={index}>
                    <h4 className="font-bold text-black mb-3">{group.category}</h4>
                    <ul className="space-y-2">
                      {group.items.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                          <Bell className="w-4 h-4 text-[#FF6B35] flex-shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Why Section */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Why Trademark Monitoring is Essential</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Registration protects your rights, but only monitoring protects your brand in practice
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <AlertCircle className="w-12 h-12 text-[#FF6B35] mb-4" />
              <h3 className="text-xl font-bold text-black mb-4">Early Detection Saves Money</h3>
              <p className="text-gray-700 mb-4">
                The earlier you catch an infringer, the less expensive enforcement becomes. Early-stage infringers:
              </p>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• Haven't invested heavily in their brand yet</li>
                <li>• Are more likely to comply with cease & desist</li>
                <li>• Haven't built customer confusion</li>
                <li>• Haven't caused significant damage</li>
                <li>• Can't claim established rights</li>
              </ul>
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-[#FF6B35] font-semibold">70% of early-detected infringers stop with a letter</p>
              </div>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <Shield className="w-12 h-12 text-[#FF6B35] mb-4" />
              <h3 className="text-xl font-bold text-black mb-4">Maintain Legal Rights</h3>
              <p className="text-gray-700 mb-4">
                Trademark law requires active policing. Failure to enforce can result in:
              </p>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• Trademark abandonment claims</li>
                <li>• Weakening of your trademark</li>
                <li>• Generic-ization of your brand</li>
                <li>• Loss of exclusive rights</li>
                <li>• Court rulings against you</li>
              </ul>
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-[#FF6B35] font-semibold">Courts favor trademark owners who actively monitor</p>
              </div>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <TrendingUp className="w-12 h-12 text-[#FF6B35] mb-4" />
              <h3 className="text-xl font-bold text-black mb-4">Preserve Brand Value</h3>
              <p className="text-gray-700 mb-4">
                Brand confusion directly impacts your business:
              </p>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• Lost sales to counterfeiters</li>
                <li>• Damaged reputation from inferior products</li>
                <li>• Customer confusion and mistrust</li>
                <li>• Dilution of brand recognition</li>
                <li>• Reduced company valuation</li>
              </ul>
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-[#FF6B35] font-semibold">Strong brands require active protection</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Monitoring Levels */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Choose Your Protection Level</h2>
            <p className="text-lg text-gray-600">Select the monitoring depth that matches your brand's needs</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {monitoringLevels.map((level, index) => (
              <div key={index} className={`bg-white rounded-xl p-8 shadow-lg border-2 ${level.popular ? 'border-[#FF6B35] transform scale-105' : 'border-gray-200'}`}>
                {level.popular && (
                  <div className="bg-[#FF6B35] text-white text-sm font-bold px-4 py-1 rounded-full inline-block mb-4">
                    Most Popular
                  </div>
                )}
                <h3 className="text-2xl font-bold text-black mb-2">{level.name}</h3>
                <div className="flex items-baseline mb-4">
                  <span className="text-4xl font-bold text-[#FF6B35]">{level.price}</span>
                  <span className="text-gray-600 ml-2">{level.period}</span>
                </div>
                <p className="text-gray-600 mb-6">{level.description}</p>
                <ul className="space-y-3 mb-6">
                  {level.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-[#FF6B35] flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <button className={`w-full py-3 rounded-lg font-semibold transition-all ${level.popular ? 'bg-[#FF6B35] text-white hover:bg-[#FF8C42]' : 'bg-gray-100 text-black hover:bg-gray-200'}`}>
                  Start Monitoring
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Threat Levels */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Understanding Threat Levels</h2>
            <p className="text-lg text-gray-600">How we categorize and prioritize potential infringements</p>
          </div>
          <div className="space-y-6">
            {threatLevels.map((threat, index) => (
              <div key={index} className={`bg-white rounded-xl p-6 border-l-4 border-${threat.color}-500 shadow-lg`}>
                <div className="flex flex-col md:flex-row md:items-center gap-6">
                  <div className="flex-shrink-0">
                    <div className={`w-20 h-20 bg-${threat.color}-100 rounded-full flex items-center justify-center`}>
                      <Target className={`w-10 h-10 text-${threat.color}-600`} />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-black mb-2">{threat.level} Threat</h3>
                    <p className="text-gray-700 mb-3">{threat.description}</p>
                    <p className="text-sm font-semibold text-[#FF6B35] mb-3">Recommended Action: {threat.action}</p>
                    <div className="flex flex-wrap gap-2">
                      {threat.examples.map((example, idx) => (
                        <span key={idx} className="bg-gray-100 px-3 py-1 rounded-full text-sm text-gray-700">
                          {example}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Case Studies */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Real-World Success Stories</h2>
            <p className="text-lg text-gray-600">See how monitoring protected these brands</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {caseStudies.map((study, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-8 shadow-lg border border-gray-200">
                <h3 className="text-xl font-bold text-black mb-4">{study.title}</h3>
                <div className="space-y-4">
                  <div>
                    <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Scenario</div>
                    <p className="text-sm text-gray-700">{study.scenario}</p>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-[#FF6B35] uppercase mb-2">Detection</div>
                    <p className="text-sm text-gray-700">{study.detection}</p>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-blue-600 uppercase mb-2">Action Taken</div>
                    <p className="text-sm text-gray-700">{study.action}</p>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-green-600 uppercase mb-2">Result</div>
                    <p className="text-sm text-gray-700 font-semibold">{study.result}</p>
                  </div>
                  <div className="pt-4 border-t border-gray-200">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Clock className="w-4 h-4" />
                      <span>{study.timeframe}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Enforcement Options */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Enforcement Options</h2>
            <p className="text-lg text-gray-600">What to do when monitoring detects infringement</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {enforcementOptions.map((option, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-black mb-3">{option.option}</h3>
                <p className="text-gray-700 mb-4 text-sm">{option.description}</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Cost:</span>
                    <span className="font-semibold text-black">{option.cost}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Success Rate:</span>
                    <span className="font-semibold text-[#FF6B35]">{option.effectiveness}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Timeline:</span>
                    <span className="font-semibold text-black">{option.timeframe}</span>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-600"><strong>Best for:</strong> {option.bestFor}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FAQs */}
      <div className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFAQ(openFAQ === index ? -1 : index)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-100 transition-colors"
                >
                  <span className="text-left font-semibold text-black pr-8">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-[#FF6B35] flex-shrink-0 transition-transform ${openFAQ === index ? 'rotate-180' : ''}`} />
                </button>
                {openFAQ === index && (
                  <div className="px-6 pb-4 text-gray-700 leading-relaxed">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <CTASection />
    </div>
  );
};

export default TrademarkMonitoringPage;